import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Calendar, MapPin, Clock, CheckCircle, Star, Loader2, X, AlertCircle, Download, Share2, TrendingUp } from 'lucide-react';
import BookingChat from '@/components/BookingChat';

const STATUS_STYLES = {
  confirmed: 'bg-emerald-50 text-emerald-700',
  completed: 'bg-emerald-100 text-emerald-800',
  pending: 'bg-amber-50 text-amber-700',
  cancelled: 'bg-red-50 text-red-700',
  rejected: 'bg-red-50 text-red-700',
  no_show: 'bg-red-100 text-red-700',
};

const STATUS_LABELS = {
  confirmed: 'Confirmed',
  completed: 'Consult Completed',
  pending: 'Pending',
  cancelled: 'Cancelled',
  rejected: 'Rejected',
  no_show: 'Did Not Attend',
};

export default function BookingsPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedbackBookingId, setFeedbackBookingId] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login?redirect=/bookings'); return; }
    loadBookings();
  }, [user, authLoading, navigate]);

  const loadBookings = () => {
    setLoading(true);
    api.get('/bookings')
      .then(r => setBookings(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await api.post('/patient/export-data', {}, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'docsite_my_data.zip');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export failed:', e);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-slate-50/50 flex items-center justify-center"><Loader2 className="animate-spin text-sky-600" size={24} /></div>;
  }

  return (
    <div className="min-h-screen bg-slate-50/50" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-10 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Appointments with Your Doctors</h1>
            <p className="text-sm text-slate-500 mt-1">Your doctor interactions and appointment history</p>
          </div>
          {bookings.length > 0 && (
            <button onClick={handleExport} disabled={exporting}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-100 text-slate-700 text-sm font-medium hover:bg-slate-200 transition-colors disabled:opacity-50"
              data-testid="export-my-data-btn">
              {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              Download My Data
            </button>
          )}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 md:px-12 py-8">
        {bookings.length === 0 ? (
          <div className="text-center py-20" data-testid="no-bookings">
            <Calendar size={40} className="text-slate-300 mx-auto mb-4" />
            <p className="text-lg text-slate-600">No bookings yet</p>
            <button onClick={() => navigate('/')} className="mt-4 rounded-full px-6 py-2.5 bg-sky-600 text-white text-sm font-medium hover:bg-sky-700 transition-colors" data-testid="find-doctor-button">
              Find a Doctor
            </button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="bookings-list">
            {bookings.map((b) => (
              <div key={b.id} className="bg-white rounded-2xl border border-slate-200 p-5 md:p-6" data-testid={`booking-${b.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-2">
                    <h3 className="font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{b.doctor_name}</h3>
                    <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                      <span className="flex items-center gap-1"><Calendar size={12} /> {b.date}</span>
                      <span className="flex items-center gap-1"><Clock size={12} /> {b.start_time} - {b.end_time}</span>
                      <span className="flex items-center gap-1"><MapPin size={12} /> {b.location_name}</span>
                    </div>
                    {b.procedure_name && <p className="text-xs text-slate-600">Procedure: {b.procedure_name}</p>}
                    <p className="text-xs text-slate-500">Type: <span className="capitalize font-medium">{b.booking_type}</span></p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${STATUS_STYLES[b.status] || 'bg-slate-100 text-slate-600'}`}>
                      {STATUS_LABELS[b.status] || b.status}
                    </span>
                    {(b.status === 'confirmed' || b.status === 'completed') && !b.has_feedback && (
                      <button
                        onClick={() => setFeedbackBookingId(b.id)}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-sky-50 text-sky-700 text-xs font-medium hover:bg-sky-100 transition-colors border border-sky-200"
                        data-testid={`give-feedback-${b.id}`}
                      >
                        <Star size={12} /> Give Feedback
                      </button>
                    )}
                    {b.has_feedback && (
                      <span className="flex items-center gap-1 px-3 py-1.5 text-xs text-emerald-600 font-medium">
                        <CheckCircle size={12} /> Feedback sent
                      </span>
                    )}
                  </div>
                </div>

                {/* Feedback Form */}
                {feedbackBookingId === b.id && (
                  <FeedbackForm
                    bookingId={b.id}
                    doctorName={b.doctor_name}
                    onClose={() => setFeedbackBookingId(null)}
                    onSubmitted={() => { setFeedbackBookingId(null); loadBookings(); }}
                  />
                )}

                {/* Booking Chat — pre-appointment coordination */}
                {(b.status === 'confirmed' || b.status === 'pending' || b.status === 'completed') && (
                  <div className="mt-3">
                    <BookingChat bookingId={b.id} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <MyReferrals />
      </div>
    </div>
  );
}

function MyReferrals() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get('/referral/my-stats').then(r => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats || stats.referrals.length === 0) return null;

  return (
    <div className="mt-8" data-testid="my-referrals-section">
      <div className="flex items-center gap-2 mb-4">
        <Share2 size={18} className="text-sky-600" />
        <h2 className="text-lg font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>My Referrals</h2>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex gap-6 mb-4 text-center">
          <div>
            <p className="text-2xl font-bold text-sky-600">{stats.totals.doctors_referred}</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Doctors Referred</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-emerald-600">{stats.totals.total_clicks}</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Link Visits</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-amber-600">{stats.totals.total_appointments}</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Appointments Made</p>
          </div>
        </div>
        <div className="space-y-2">
          {stats.referrals.map(r => (
            <div key={r.ref_code} className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-100">
              <div>
                <p className="text-sm font-medium text-slate-900">{r.doctor_name}</p>
                <p className="text-[10px] text-slate-500">Code: {r.ref_code}</p>
              </div>
              <div className="flex items-center gap-4 text-xs text-slate-600">
                <span className="flex items-center gap-1"><TrendingUp size={12} className="text-sky-500" /> {r.clicks} visits</span>
                <span className="flex items-center gap-1"><CheckCircle size={12} className="text-emerald-500" /> {r.appointments} appts</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}



function FeedbackForm({ bookingId, doctorName, onClose, onSubmitted }) {
  const [q1, setQ1] = useState(null);       // could_explain_symptoms
  const [q2a, setQ2a] = useState(null);      // doctor_explained_condition
  const [q2b, setQ2b] = useState(null);      // doctor_explained_treatment
  const [q3, setQ3] = useState('');          // symptoms_resolved: fully/partially/no
  const [q4, setQ4] = useState(null);        // interactions_helpful
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const allAnswered = q1 !== null && q2a !== null && q2b !== null && q3 !== '' && q4 !== null;

  const handleSubmit = async () => {
    if (!allAnswered) { setError('Please answer all questions'); return; }
    setSubmitting(true); setError('');
    try {
      await api.post(`/bookings/${bookingId}/feedback`, {
        could_explain_symptoms: q1,
        doctor_explained_condition: q2a,
        doctor_explained_treatment: q2b,
        symptoms_resolved: q3,
        interactions_helpful: q4,
      });
      onSubmitted();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const YesNo = ({ value, onChange, testid }) => (
    <div className="flex gap-2">
      <button onClick={() => onChange(true)}
        className={`px-5 py-2 rounded-xl text-xs font-medium transition-all border ${value === true ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}
        data-testid={`${testid}-yes`}>Yes</button>
      <button onClick={() => onChange(false)}
        className={`px-5 py-2 rounded-xl text-xs font-medium transition-all border ${value === false ? 'bg-red-50 border-red-300 text-red-700' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}
        data-testid={`${testid}-no`}>No</button>
    </div>
  );

  return (
    <div className="mt-4 pt-4 border-t border-slate-100" data-testid={`feedback-form-${bookingId}`}>
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Help us understand your experience with {doctorName}
        </p>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={16} /></button>
      </div>

      <div className="space-y-5">
        {/* Q1 */}
        <div>
          <p className="text-xs font-medium text-slate-700 mb-2">1. Were you able to explain all your symptoms in detail to the doctor?</p>
          <YesNo value={q1} onChange={setQ1} testid="q1" />
        </div>

        {/* Q2 */}
        <div className="space-y-3">
          <p className="text-xs font-medium text-slate-700">2. Did the doctor explain clearly:</p>
          <div className="pl-3 space-y-3">
            <div>
              <p className="text-xs text-slate-600 mb-1.5">a) What was likely happening with your condition?</p>
              <YesNo value={q2a} onChange={setQ2a} testid="q2a" />
            </div>
            <div>
              <p className="text-xs text-slate-600 mb-1.5">b) What they would do for you and why?</p>
              <YesNo value={q2b} onChange={setQ2b} testid="q2b" />
            </div>
          </div>
        </div>

        {/* Q3 */}
        <div>
          <p className="text-xs font-medium text-slate-700 mb-2">3. After treatment, did the condition or symptoms improve?</p>
          <div className="flex gap-2">
            {[
              { value: 'fully', label: 'Fully resolved', color: 'emerald' },
              { value: 'partially', label: 'Partially improved', color: 'amber' },
              { value: 'no', label: 'No improvement', color: 'red' },
            ].map(opt => (
              <button key={opt.value} onClick={() => setQ3(opt.value)}
                className={`px-4 py-2 rounded-xl text-xs font-medium transition-all border ${
                  q3 === opt.value
                    ? opt.color === 'emerald' ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                    : opt.color === 'amber' ? 'bg-amber-50 border-amber-300 text-amber-700'
                    : 'bg-red-50 border-red-300 text-red-700'
                    : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                }`}
                data-testid={`q3-${opt.value}`}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Q4 */}
        <div>
          <p className="text-xs font-medium text-slate-700 mb-2">4. Overall, were the interactions with the doctor helpful?</p>
          <YesNo value={q4} onChange={setQ4} testid="q4" />
        </div>

        {error && <p className="text-xs text-red-600 flex items-center gap-1"><AlertCircle size={12} /> {error}</p>}

        <button onClick={handleSubmit} disabled={submitting || !allAnswered}
          className="rounded-full px-6 py-2.5 bg-sky-600 text-white text-sm font-medium hover:bg-sky-700 transition-colors disabled:opacity-50"
          data-testid="submit-feedback-button">
          {submitting ? 'Submitting...' : 'Submit Feedback'}
        </button>
      </div>
    </div>
  );
}
